import { Button, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconBook, IconExternalLink, IconMailFast } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '~/trpc-extra/api';

export type SupportMenuItem = {
  label: string;
  icon: typeof IconBook;
  rightSection?: ReactNode;
  onClick: () => void;
};

function FeedbackModalContent({ onSubmit }: { onSubmit: (feedback: string) => Promise<void> }) {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const handleSubmit = async () => {
    const trimmedFeedback = feedbackText.trim();
    if (!trimmedFeedback) {
      notifications.show({ message: 'Please enter feedback before submitting.', color: 'yellow' });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(trimmedFeedback);
    } catch {
      notifications.show({ message: 'Failed to submit feedback. Please try again.', color: 'red' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <Textarea
        ref={textareaRef}
        autoFocus
        data-autofocus
        minRows={6}
        maxRows={12}
        autosize
        radius="md"
        placeholder="What could we improve?"
        value={feedbackText}
        onChange={(event) => setFeedbackText(event.currentTarget.value)}
      />
      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!feedbackText.trim() || isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
}

export function useSupportMenuItems({
  projectId,
  pathname,
}: {
  projectId?: string;
  pathname?: string;
}): SupportMenuItem[] {
  const submitInstantFeedback = api.user.submitInstantFeedback.useMutation();

  return useMemo(
    () => [
      {
        label: 'Docs',
        icon: IconBook,
        rightSection: <IconExternalLink size={14} />,
        onClick: () => window.open('https://docs.stably.ai', '_blank'),
      },
      {
        label: 'Book a Call',
        icon: IconExternalLink,
        rightSection: <IconExternalLink size={14} />,
        onClick: () => window.open('https://calendly.com/jinjing/stably-guided-test-creation?back=1', '_blank'),
      },
      {
        label: 'Send Feedback',
        icon: IconMailFast,
        onClick: () => {
          window.setTimeout(() => {
            const modalId = modals.open({
              title: 'Send Feedback',
              centered: true,
              size: 'lg',
              children: (
                <FeedbackModalContent
                  onSubmit={async (feedback) => {
                    await submitInstantFeedback.mutateAsync({
                      feedback,
                      projectId: projectId || undefined,
                      pathname: pathname || undefined,
                    });
                    notifications.show({ message: 'Thanks for the feedback.', color: 'green' });
                    modals.close(modalId);
                  }}
                />
              ),
            });
          }, 0);
        },
      },
    ],
    [pathname, projectId, submitInstantFeedback],
  );
}
